/**
 * Storage Service - Handles Storage and LegacyStorage API operations
 *
 * This service provides unified access to:
 * - /api/Storage - Current MongoDB database (state-adv-charts)
 * - /api/LegacyStorage - Legacy MongoDB database (statehtml)
 *
 * Both APIs have identical endpoints:
 * - GET /api/Storage?kind=X&chartid=Y - List items or get specific item
 * - POST /api/Storage - Save or update item
 * - DELETE /api/Storage?kind=X&chartid=Y - Delete item
 *
 * Common 'kind' types: chart, template, layout, watchlist, strategy, settings
 */

import * as utilities from "./utilites";
import marketdata from "./marketdata.jsx";

// Match marketdata.jsx implementation exactly
const STORAGE_COMMAND = "/api/storage";
const LEGACY_STORAGE_COMMAND = "/api/legacystorage";
const SILVERLIGHT_STORAGE_COMMAND = "/api/silverlightstorage";

class StorageService {
  constructor() {
    this.baseUrl = null;
    this.token = null;
  }

  /**
   * Initialize the storage service with base URL and authentication token
   * @param {string} baseUrl - Base URL for the API (e.g., 'http://localhost:5000')
   * @param {string} token - JWT authentication token
   */
  init(baseUrl, token) {
    this.baseUrl = baseUrl;
    this.token = token;
    console.log("[StorageService] Initialized successfully");
  }

  /**
   * Set authentication token
   * @param {string} token - JWT authentication token
   */
  setToken(token) {
    this.token = token;
  }

  /**
   * Get base URL for storage API
   * @private
   */
  getUrl() {
    if (!this.baseUrl) {
      console.warn("[StorageService] Not initialized! Call init() first.");
    }
    return this.baseUrl;
  }

  /**
   * Check if service is initialized
   * @returns {boolean}
   */
  isInitialized() {
    return this.baseUrl !== null && this.token !== null;
  }

  // ============================================================================
  // CURRENT STORAGE API (/api/Storage)
  // ============================================================================

  /**
   * Auto-initialize from marketdata if needed
   * @private
   */
  ensureInitialized() {
    if (!this.isInitialized()) {
      console.warn(
        "[StorageService] Not initialized, attempting auto-init from marketdata...",
      );
      // Try to auto-initialize from marketdata
      if (marketdata && marketdata.signalRToken && marketdata.getUrl) {
        const url = marketdata.getUrl();
        const token = marketdata.signalRToken;
        if (url && token) {
          console.log("[StorageService] Auto-initializing from marketdata");
          this.init(url, token);
        } else {
          console.error("[StorageService] Marketdata URL or token missing");
        }
      } else {
        console.error(
          "[StorageService] Cannot auto-init: marketdata not ready",
        );
      }
    }
  }

  /**
   * Get a valid authentication token, refreshing if needed
   * @private
   * @returns {Promise<string>} - Valid JWT token
   */
  async getValidToken() {
    // If we have marketdata available with getValidToken method, use it
    if (marketdata && typeof marketdata.getValidToken === "function") {
      try {
        const freshToken = await marketdata.getValidToken();
        // Update our cached token to stay in sync
        this.token = freshToken;
        return freshToken;
      } catch (error) {
        console.error(
          "[StorageService] Failed to get valid token from marketdata:",
          error,
        );
        // Fall back to cached token if available
        return this.token;
      }
    }

    // Fall back to cached token
    return this.token;
  }

  /**
   * Get list of items from current storage
   * @param {string} kind - Item category (e.g., 'chart', 'template', 'layout')
   * @returns {Promise<Array>} - List of items
   */
  async getList(kind) {
    this.ensureInitialized();

    if (!this.isInitialized()) {
      console.error(
        `[StorageService] Cannot get ${kind} list - service not initialized`,
      );
      return [];
    }

    const url = `${this.getUrl()}${STORAGE_COMMAND}?kind=${kind}&_=${new Date().getTime()}`;

    try {
      const token = await this.getValidToken();
      const resp = await utilities.getStringFromURL(url, token);
      const r = JSON.parse(resp);
      if (r.status && r.status === "ok") {
        return r.data;
      }
      return [];
    } catch (error) {
      console.error(`[StorageService] Failed to get ${kind} list:`, error);
      utilities.errorHandler(error);
      return [];
    }
  }

  /**
   * Get specific item from current storage
   * @param {string} kind - Item category
   * @param {number} chartid - Item ID
   * @returns {Promise<Object|null>} - Item data or null
   */
  async getItem(kind, chartid) {
    const url = `${this.getUrl()}${STORAGE_COMMAND}?kind=${kind}&chartid=${chartid}&_=${new Date().getTime()}`;
    try {
      const token = await this.getValidToken();
      const resp = await utilities.getStringFromURL(url, token);
      const r = JSON.parse(resp);
      if (r.status && r.status === "ok") {
        return r.data;
      }
      return null;
    } catch (error) {
      console.error(
        `[StorageService] Failed to get ${kind} item ${chartid}:`,
        error,
      );
      utilities.errorHandler(error);
      return null;
    }
  }

  /**
   * Save or update item in current storage
   * @param {string} kind - Item category
   * @param {number} chartid - Item ID (-1 to create new)
   * @param {string} name - Item name
   * @param {string} content - Item content (JSON string)
   * @returns {Promise<number|null>} - Saved item ID or null
   */
  async saveItem(kind, chartid, name, content) {
    const url = `${this.getUrl()}${STORAGE_COMMAND}`;
    const request = {
      kind,
      chartid,
      name,
      content,
    };
    try {
      const token = await this.getValidToken();
      const resp = await utilities.postJsonToURL(url, request, token);
      const r = JSON.parse(resp);
      if (r.status && r.status === "ok") {
        return r.data;
      }
      return null;
    } catch (error) {
      console.error(`[StorageService] Failed to save ${kind} item:`, error);
      utilities.errorHandler(error);
      return null;
    }
  }

  /**
   * Delete item from current storage
   * @param {string} kind - Item category
   * @param {number} chartid - Item ID
   * @returns {Promise<boolean>} - Success status
   */
  async deleteItem(kind, chartid) {
    const url = `${this.getUrl()}${STORAGE_COMMAND}?kind=${kind}&chartid=${chartid}`;
    try {
      const token = await this.getValidToken();
      await utilities.deleteFromURL(url, token);
      return true;
    } catch (error) {
      console.error(
        `[StorageService] Failed to delete ${kind} item ${chartid}:`,
        error,
      );
      utilities.errorHandler(error);
      return false;
    }
  }

  // ============================================================================
  // LEGACY STORAGE API (/api/LegacyStorage)
  // ============================================================================

  /**
   * Get list of items from legacy storage
   * @param {string} kind - Item category (e.g., 'chart', 'template', 'layout')
   * @returns {Promise<Array>} - List of items
   */
  async getLegacyList(kind) {
    this.ensureInitialized();
    const url = `${this.getUrl()}${LEGACY_STORAGE_COMMAND}?kind=${kind}&_=${new Date().getTime()}`;
    try {
      const token = await this.getValidToken();
      const resp = await utilities.getStringFromURL(url, token);
      const r = JSON.parse(resp);
      if (r.status && r.status === "ok") {
        return r.data;
      }
      return [];
    } catch (error) {
      console.error(
        `[StorageService] Failed to get legacy ${kind} list:`,
        error,
      );
      utilities.errorHandler(error);
      return [];
    }
  }

  /**
   * Get specific item from legacy storage
   * @param {string} kind - Item category
   * @param {number} chartid - Item ID
   * @returns {Promise<Object|null>} - Item data or null
   */
  async getLegacyItem(kind, chartid) {
    this.ensureInitialized();
    const url = `${this.getUrl()}${LEGACY_STORAGE_COMMAND}?kind=${kind}&chartid=${chartid}&_=${new Date().getTime()}`;
    try {
      const token = await this.getValidToken();
      const resp = await utilities.getStringFromURL(url, token);
      const r = JSON.parse(resp);
      if (r.status && r.status === "ok") {
        return r.data;
      }
      return null;
    } catch (error) {
      console.error(
        `[StorageService] Failed to get legacy ${kind} item ${chartid}:`,
        error,
      );
      utilities.errorHandler(error);
      return null;
    }
  }

  /**
   * Save or update item in legacy storage
   * @param {string} kind - Item category
   * @param {number} chartid - Item ID (-1 to create new)
   * @param {string} name - Item name
   * @param {string} content - Item content (JSON string)
   * @returns {Promise<number|null>} - Saved item ID or null
   */
  async saveLegacyItem(kind, chartid, name, content) {
    this.ensureInitialized();
    const url = `${this.getUrl()}${LEGACY_STORAGE_COMMAND}`;
    const request = {
      kind,
      chartid,
      name,
      content,
    };
    try {
      const token = await this.getValidToken();
      const resp = await utilities.postJsonToURL(url, request, token);
      const r = JSON.parse(resp);
      if (r.status && r.status === "ok") {
        return r.data;
      }
      return null;
    } catch (error) {
      console.error(
        `[StorageService] Failed to save legacy ${kind} item:`,
        error,
      );
      utilities.errorHandler(error);
      return null;
    }
  }

  /**
   * Delete item from legacy storage
   * @param {string} kind - Item category
   * @param {number} chartid - Item ID
   * @returns {Promise<boolean>} - Success status
   */
  async deleteLegacyItem(kind, chartid) {
    this.ensureInitialized();
    const url = `${this.getUrl()}${LEGACY_STORAGE_COMMAND}?kind=${kind}&chartid=${chartid}`;
    try {
      const token = await this.getValidToken();
      await utilities.deleteFromURL(url, token);
      return true;
    } catch (error) {
      console.error(
        `[StorageService] Failed to delete legacy ${kind} item ${chartid}:`,
        error,
      );
      utilities.errorHandler(error);
      return false;
    }
  }

  // ============================================================================
  // CONVENIENCE METHODS - TEMPLATES (Current Storage)
  // ============================================================================

  /**
   * Get list of templates from current storage
   * @returns {Promise<Array>} - List of templates
   */
  async getTemplateList() {
    return this.getList("template");
  }

  /**
   * Get specific template from current storage
   * @param {number} id - Template ID
   * @returns {Promise<Object|null>} - Template data or null
   */
  async getTemplate(id) {
    return this.getItem("template", id);
  }

  /**
   * Save template to current storage
   * @param {number} id - Template ID (-1 to create new)
   * @param {string} name - Template name
   * @param {string} content - Template content (JSON string)
   * @returns {Promise<number|null>} - Saved template ID or null
   */
  async saveTemplate(id, name, content) {
    return this.saveItem("template", id, name, content);
  }

  /**
   * Delete template from current storage
   * @param {number} id - Template ID
   * @returns {Promise<boolean>} - Success status
   */
  async deleteTemplate(id) {
    return this.deleteItem("template", id);
  }

  // ============================================================================
  // CONVENIENCE METHODS - CHARTS (Current Storage)
  // ============================================================================

  /**
   * Get list of charts from current storage
   * @returns {Promise<Array>} - List of charts
   */
  async getChartList() {
    return this.getList("chart");
  }

  /**
   * Get specific chart from current storage
   * @param {number} id - Chart ID
   * @returns {Promise<Object|null>} - Chart data or null
   */
  async getChart(id) {
    return this.getItem("chart", id);
  }

  /**
   * Save chart to current storage
   * @param {number} id - Chart ID (-1 to create new)
   * @param {string} name - Chart name
   * @param {string} content - Chart content (JSON string)
   * @returns {Promise<number|null>} - Saved chart ID or null
   */
  async saveChart(id, name, content) {
    return this.saveItem("chart", id, name, content);
  }

  /**
   * Delete chart from current storage
   * @param {number} id - Chart ID
   * @returns {Promise<boolean>} - Success status
   */
  async deleteChart(id) {
    return this.deleteItem("chart", id);
  }

  // ============================================================================
  // CONVENIENCE METHODS - DESKTOPS (Current Storage)
  // ============================================================================

  /**
   * Get list of desktops from current storage
   * @returns {Promise<Array>} - List of desktops
   */
  async getDesktopList() {
    return this.getList("desktop");
  }

  /**
   * Get specific desktop from current storage
   * @param {number} id - Desktop ID
   * @returns {Promise<Object|null>} - Desktop data or null
   */
  async getDesktop(id) {
    return this.getItem("desktop", id);
  }

  /**
   * Save desktop to current storage
   * @param {number} id - Desktop ID (-1 to create new)
   * @param {string} name - Desktop name
   * @param {string} content - Desktop content (JSON string)
   * @returns {Promise<number|null>} - Saved desktop ID or null
   */
  async saveDesktop(id, name, content) {
    return this.saveItem("desktop", id, name, content);
  }

  /**
   * Delete desktop from current storage
   * @param {number} id - Desktop ID
   * @returns {Promise<boolean>} - Success status
   */
  async deleteDesktop(id) {
    return this.deleteItem("desktop", id);
  }

  // ============================================================================
  // SILVERLIGHT STORAGE API (/api/SilverlightStorage) — SOMENTE LEITURA
  // Desktops/templates do app Silverlight legado (MySQL do serviço State).
  // PK é (login, name) — NÃO existe id. Lista vem SEM payload; o item completo
  // (properties base64/zip) só no GET por name.
  // ============================================================================

  /**
   * Get list of items from Silverlight legacy storage
   * @param {string} kind - 'desktop' | 'template'
   * @returns {Promise<Array>} - List of items ({name, isDefault, obs, lastUpdate})
   */
  async getSilverlightList(kind) {
    this.ensureInitialized();
    const url = `${this.getUrl()}${SILVERLIGHT_STORAGE_COMMAND}?kind=${kind}&_=${new Date().getTime()}`;
    try {
      const token = await this.getValidToken();
      const resp = await utilities.getStringFromURL(url, token);
      const r = JSON.parse(resp);
      if (r.status && r.status === "ok") {
        return r.data;
      }
      return [];
    } catch (error) {
      console.error(
        `[StorageService] Failed to get silverlight ${kind} list:`,
        error,
      );
      utilities.errorHandler(error);
      return [];
    }
  }

  /**
   * Get specific item (with payload) from Silverlight legacy storage
   * @param {string} kind - 'desktop' | 'template'
   * @param {string} name - Item name (PK junto com o login; não há id)
   * @returns {Promise<Object|null>} - Item data or null
   */
  async getSilverlightItem(kind, name) {
    this.ensureInitialized();
    const url = `${this.getUrl()}${SILVERLIGHT_STORAGE_COMMAND}?kind=${kind}&name=${encodeURIComponent(name)}&_=${new Date().getTime()}`;
    try {
      const token = await this.getValidToken();
      const resp = await utilities.getStringFromURL(url, token);
      const r = JSON.parse(resp);
      if (r.status && r.status === "ok") {
        return r.data;
      }
      return null;
    } catch (error) {
      console.error(
        `[StorageService] Failed to get silverlight ${kind} item ${name}:`,
        error,
      );
      utilities.errorHandler(error);
      return null;
    }
  }

  /**
   * Get list of desktops from Silverlight legacy storage
   * @returns {Promise<Array>} - List of Silverlight desktops
   */
  async getSilverlightDesktopList() {
    return this.getSilverlightList("desktop");
  }

  /**
   * Get list of templates from Silverlight legacy storage
   * @returns {Promise<Array>} - List of Silverlight templates
   */
  async getSilverlightTemplateList() {
    return this.getSilverlightList("template");
  }

  // ============================================================================
  // CONVENIENCE METHODS - LEGACY CHARTS
  // ============================================================================

  /**
   * Get list of charts from legacy storage
   * @returns {Promise<Array>} - List of legacy charts
   */
  async getLegacyChartList() {
    return this.getLegacyList("chart");
  }

  /**
   * Get specific chart from legacy storage
   * @param {number} id - Chart ID
   * @returns {Promise<Object|null>} - Legacy chart data or null
   */
  async getLegacyChart(id) {
    return this.getLegacyItem("chart", id);
  }

  // ============================================================================
  // CONVENIENCE METHODS - LEGACY DESKTOPS
  // ============================================================================

  /**
   * Get list of desktops from legacy storage
   * @returns {Promise<Array>} - List of legacy desktops
   */
  async getLegacyDesktopList() {
    return this.getLegacyList("desktop");
  }

  /**
   * Get specific desktop from legacy storage
   * @param {number} id - Desktop ID
   * @returns {Promise<Object|null>} - Legacy desktop data or null
   */
  async getLegacyDesktop(id) {
    return this.getLegacyItem("desktop", id);
  }

  // ============================================================================
  // CONVENIENCE METHODS - LEGACY TEMPLATES
  // ============================================================================

  /**
   * Get list of templates from legacy storage
   * @returns {Promise<Array>} - List of legacy templates
   */
  async getLegacyTemplateList() {
    try {
      const list = await this.getLegacyList("templates");

      if (!list || list.length === 0) {
        return [];
      }

      // 🔍 AUTO-DETECT: Is this the legacy wrapper format?
      // Legacy: exactly 1 item with chartid=1 and name="templates"
      if (
        list.length === 1 &&
        list[0].chartid === 1 &&
        list[0].name === "templates"
      ) {
        console.log(
          "[StorageService] Detected legacy template format - unwrapping...",
        );
        return await this._unwrapLegacyTemplates(list[0]);
      }

      // ✅ Already unwrapped (future server fix or different format)
      console.log("[StorageService] Detected modern template format");
      return list;
    } catch (error) {
      console.error("[StorageService] Failed to get legacy templates:", error);
      return [];
    }
  }

  async _unwrapLegacyTemplates(wrapper) {
    try {
      // Get the full document with content
      const fullDoc = await this.getLegacyItem("templates", 1);

      if (!fullDoc || !fullDoc.content) {
        console.error(
          "[StorageService] Legacy template wrapper has no content",
        );
        return [];
      }

      // Parse the content - it's an array like:
      // [{"bvbgv": {...}}, {"222": {...}}, {"333": {...}}]
      const templatesArray = JSON.parse(fullDoc.content);

      if (!Array.isArray(templatesArray)) {
        console.error(
          "[StorageService] Template content is not an array:",
          typeof templatesArray,
        );
        return [];
      }

      // Flatten: each array element is an object with one key (template name)
      const unwrapped = [];
      let virtualId = 0;

      templatesArray.forEach((templateObj) => {
        // Each templateObj is like {"bvbgv": {...template data...}}
        Object.entries(templateObj).forEach(([name, data]) => {
          unwrapped.push({
            chartid: virtualId++, // Virtual ID for UI (not used for fetching)
            name: name,
            timestamp: wrapper.timestamp,
            kind: "template",
            content: JSON.stringify(data), // Store as JSON string for consistency
            _isUnwrapped: true, // Flag to identify unwrapped templates
            _templateData: data, // Store parsed data for direct access
          });
        });
      });

      console.log(
        `[StorageService] Unwrapped ${unwrapped.length} legacy templates:`,
        unwrapped.map((t) => t.name),
      );
      return unwrapped;
    } catch (error) {
      console.error(
        "[StorageService] Failed to unwrap legacy templates:",
        error,
      );
      return [];
    }
  }

  /**
   * Get specific template from legacy storage
   * @param {number} id - Template ID
   * @returns {Promise<Object|null>} - Legacy template data or null
   */
  async getLegacyTemplate(id) {
    return this.getLegacyItem("templates", id);
  }

  // ============================================================================
  // MIGRATION HELPER METHODS
  // ============================================================================

  /**
   * Migrate item from legacy storage to current storage
   * @param {string} kind - Item category
   * @param {number} legacyChartId - Legacy item ID
   * @param {string} newName - Optional new name (uses legacy name if not provided)
   * @returns {Promise<number|null>} - New item ID in current storage or null
   */
  async migrateLegacyItem(kind, legacyChartId, newName = null) {
    try {
      console.log(
        `[StorageService] Migrating legacy ${kind} ${legacyChartId}...`,
      );

      // Get item from legacy storage
      const legacyItem = await this.getLegacyItem(kind, legacyChartId);
      if (!legacyItem) {
        console.error(
          `[StorageService] Legacy ${kind} ${legacyChartId} not found`,
        );
        return null;
      }

      // Save to current storage with new ID
      const name = newName || legacyItem.name;
      const newId = await this.saveItem(kind, -1, name, legacyItem.content);

      if (newId) {
        console.log(
          `[StorageService] Migrated legacy ${kind} ${legacyChartId} to new ID ${newId}`,
        );
      }

      return newId;
    } catch (error) {
      console.error(
        `[StorageService] Failed to migrate legacy ${kind} ${legacyChartId}:`,
        error,
      );
      return null;
    }
  }

  /**
   * Migrate all items of a specific kind from legacy storage to current storage
   * @param {string} kind - Item category
   * @returns {Promise<Object>} - Migration results {success: number, failed: number, total: number}
   */
  async migrateAllLegacyItems(kind) {
    try {
      console.log(
        `[StorageService] Starting migration of all legacy ${kind} items...`,
      );

      // Get list from legacy storage
      const legacyItems = await this.getLegacyList(kind);
      const total = legacyItems.length;
      let success = 0;
      let failed = 0;

      console.log(
        `[StorageService] Found ${total} legacy ${kind} items to migrate`,
      );

      for (const item of legacyItems) {
        const result = await this.migrateLegacyItem(kind, item.chartid);
        if (result) {
          success++;
        } else {
          failed++;
        }
      }

      const results = { success, failed, total };
      console.log(`[StorageService] Migration complete:`, results);
      return results;
    } catch (error) {
      console.error(
        `[StorageService] Failed to migrate all legacy ${kind} items:`,
        error,
      );
      return { success: 0, failed: 0, total: 0, error: error.message };
    }
  }
}

// Create singleton instance
const storageService = new StorageService();

export default storageService;
